import { z } from 'zod';
import { upsertDietaryProfileSchema } from './canteen-dietary.js';
/** Public, versioned contract for the independent Workflow product. */
export const workflowApiVersionSchema = z.literal('1');
export const workflowIdSchema = z.string().uuid();
export const workflowDateTimeSchema = z.string().datetime({ offset: true });
export const workflowCaseTypeSchema = z.enum([
    'canteen.dietary-exception',
    'canteen.reservation-change',
    'canteen.reservation-correction',
    'canteen.product-withdrawal',
    'canteen.batch-recall',
    'canteen.approval-required',
]);
export const workflowCaseStateSchema = z.enum([
    'draft',
    'submitted',
    'in_review',
    'awaiting_domain_effect',
    'completed',
    'rejected',
    'cancelled',
    'reconciliation_required',
]);
export const workflowPermissionSchema = z.enum([
    'workflow.case.create',
    'workflow.case.read',
    'workflow.case.transition',
    'workflow.task.claim',
    'workflow.task.assign',
    'workflow.decision.record',
    'workflow.definition.manage',
    'workflow.migration.run',
    'workflow.audit.read',
]);
const shortText = z.string().trim().min(1).max(500);
const opaqueReferenceSchema = z.object({
    provider: z.literal('dms'),
    documentId: workflowIdSchema,
    versionId: workflowIdSchema,
    displayName: z.string().trim().min(1).max(200),
    sha256: z.string().regex(/^sha256:[a-f0-9]{64}$/),
});
export const workflowFieldSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    label: shortText,
    type: z.enum(['text', 'number', 'date', 'select', 'attachment']),
    required: z.boolean().default(false),
    sensitive: z.boolean().default(false),
    allowedValues: z.array(z.string().max(120)).default([]),
});
export const workflowTransitionSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    from: workflowCaseStateSchema,
    to: workflowCaseStateSchema,
    label: shortText,
    requiredPermissions: z.array(workflowPermissionSchema).min(1),
    requiredFields: z.array(z.string()).default([]),
    createsTask: z.boolean().default(false),
    requiresDecision: z.boolean().default(false),
});
export const workflowDefinitionDraftSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9.-]{2,99}$/),
    name: shortText,
    caseType: workflowCaseTypeSchema,
    classification: z.enum(['internal', 'confidential', 'restricted']),
    fields: z.array(workflowFieldSchema).max(100),
    transitions: z.array(workflowTransitionSchema).min(1).max(100),
    approvalPolicy: z.object({
        minimumApprovals: z.number().int().min(1).max(10).default(1),
        prohibitSelfApproval: z.boolean().default(true),
        requireDifferentActors: z.boolean().default(true),
        allowExceptionalOverride: z.boolean().default(false),
    }),
    slaHours: z
        .number()
        .int()
        .min(1)
        .max(24 * 365)
        .default(72),
});
export const workflowDefinitionSchema = workflowDefinitionDraftSchema.extend({
    id: workflowIdSchema,
    version: z.number().int().positive(),
    status: z.enum(['draft', 'published', 'superseded', 'retired']),
    publishedAt: workflowDateTimeSchema.nullable(),
    createdAt: workflowDateTimeSchema,
    createdBy: workflowIdSchema,
});
export const canteenEffectTypeSchema = z.enum([
    'dietary_exception.apply',
    'reservation.change.apply',
    'reservation.correction.apply',
    'product.withdraw.apply',
    'batch.recall.apply',
    'approval.record.apply',
]);
/** An opaque effect envelope is not authority to execute its payload. */
export const canteenEffectSchema = z.object({
    contractVersion: workflowApiVersionSchema,
    effectType: canteenEffectTypeSchema,
    targetId: workflowIdSchema,
    // Revision zero is the explicit optimistic-concurrency version for a
    // not-yet-created dietary profile; other v1 effect types stay fail-closed.
    expectedVersion: z.number().int().nonnegative().nullable(),
    payload: z.record(z.string(), z.unknown()),
});
export const canteenEffectMappingSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('rejected'),
        reasonCode: z.literal('canteen_effect_contract_unsupported'),
    }),
    z.object({
        kind: z.literal('dietary_profile_upsert'),
        targetId: workflowIdSchema,
        expectedRevision: z.number().int().nonnegative(),
        input: upsertDietaryProfileSchema,
    }),
]);
/**
 * Fail closed at the product boundary. Reservation, withdrawal, recall and
 * approval payloads are not published Canteen command contracts and may not
 * be forwarded merely because their names look actionable.
 */
export function mapCanteenWorkflowEffect(effect) {
    if (effect.effectType !== 'dietary_exception.apply')
        return {
            kind: 'rejected',
            reasonCode: 'canteen_effect_contract_unsupported',
        };
    const input = upsertDietaryProfileSchema.safeParse(effect.payload);
    if (!input.success || effect.expectedVersion === null)
        return {
            kind: 'rejected',
            reasonCode: 'canteen_effect_contract_unsupported',
        };
    if (input.data.expectedRevision !== effect.expectedVersion)
        return {
            kind: 'rejected',
            reasonCode: 'canteen_effect_contract_unsupported',
        };
    return {
        kind: 'dietary_profile_upsert',
        targetId: effect.targetId,
        expectedRevision: effect.expectedVersion,
        input: input.data,
    };
}
export const createWorkflowCaseSchema = z.object({
    contractVersion: workflowApiVersionSchema,
    definitionId: workflowIdSchema,
    definitionVersion: z.number().int().positive(),
    caseType: workflowCaseTypeSchema,
    correlationId: workflowIdSchema,
    domain: z.literal('canteen').optional(),
    domainReference: z
        .object({
        type: z.string().min(1).max(100),
        id: workflowIdSchema,
        display: z.string().max(200).optional(),
    })
        .optional(),
    data: z.record(z.string(), z.unknown()).default({}),
    attachments: z.array(opaqueReferenceSchema).max(50).default([]),
    requestedEffect: canteenEffectSchema.optional(),
});
export const caseTransitionCommandSchema = z.object({
    transitionId: z.string().min(1).max(64),
    expectedVersion: z.number().int().positive(),
    dataPatch: z.record(z.string(), z.unknown()).default({}),
    reason: z.string().trim().min(3).max(500).optional(),
});
export const workflowTaskSchema = z.object({
    id: workflowIdSchema,
    caseId: workflowIdSchema,
    title: shortText,
    state: z.enum(['open', 'claimed', 'completed', 'escalated', 'cancelled']),
    queue: z.string().min(1).max(100),
    assigneeId: workflowIdSchema.nullable(),
    dueAt: workflowDateTimeSchema.nullable(),
    version: z.number().int().positive(),
    createdAt: workflowDateTimeSchema,
});
export const delegateTaskSchema = z.object({
    delegateTo: workflowIdSchema,
    reason: z.string().trim().min(3).max(500),
    validUntil: workflowDateTimeSchema,
    expectedVersion: z.number().int().positive(),
});
export const workflowDecisionSchema = z.object({
    outcome: z.enum(['approved', 'rejected', 'request_changes', 'exception']),
    expectedCaseVersion: z.number().int().positive(),
    comment: z.string().trim().min(3).max(2_000),
    step: z.number().int().positive().default(1),
    reauthenticated: z.boolean().default(false),
    exceptionReason: z.string().trim().min(3).max(500).optional(),
});
export const workflowCaseSchema = z.object({
    id: workflowIdSchema,
    institutionId: workflowIdSchema,
    definitionId: workflowIdSchema,
    definitionVersion: z.number().int().positive(),
    caseType: workflowCaseTypeSchema,
    state: workflowCaseStateSchema,
    version: z.number().int().positive(),
    initiatorId: workflowIdSchema,
    ownerId: workflowIdSchema.nullable(),
    correlationId: workflowIdSchema,
    domainReference: z
        .object({ type: z.string(), id: workflowIdSchema })
        .nullable(),
    data: z.record(z.string(), z.unknown()),
    attachments: z.array(opaqueReferenceSchema),
    dueAt: workflowDateTimeSchema.nullable(),
    effectStatus: z.enum([
        'none',
        'pending',
        'accepted',
        'rejected',
        'ambiguous',
        'reconciliation_required',
    ]),
    createdAt: workflowDateTimeSchema,
    updatedAt: workflowDateTimeSchema,
});
export const workflowHistoryEntrySchema = z.object({
    id: workflowIdSchema,
    caseId: workflowIdSchema,
    kind: z.enum([
        'created',
        'transitioned',
        'task_claimed',
        'delegated',
        'decision',
        'timer',
        'effect_requested',
        'effect_result',
        'migration',
    ]),
    actorId: workflowIdSchema.nullable(),
    occurredAt: workflowDateTimeSchema,
    data: z.record(z.string(), z.unknown()),
});
export const canteenWorkflowEffectResultSchema = z.object({
    contractVersion: workflowApiVersionSchema,
    commandId: workflowIdSchema,
    correlationId: workflowIdSchema,
    status: z.enum(['accepted', 'rejected', 'ambiguous']),
    domainReceiptId: workflowIdSchema.optional(),
    reasonCode: z.string().max(100).optional(),
    currentVersion: z.number().int().positive().optional(),
    occurredAt: workflowDateTimeSchema,
});
export const workflowDomainCommandSchema = z.object({
    commandId: workflowIdSchema,
    eventType: z.literal('workflow.canteen.effect-requested.v1'),
    schemaVersion: z.literal(1),
    caseId: workflowIdSchema,
    correlationId: workflowIdSchema,
    idempotencyKey: z.string().min(16).max(200),
    issuedAt: workflowDateTimeSchema,
    effect: canteenEffectSchema,
});
/** The only executable Workflow → Canteen effect in v2. */
export const workflowDietaryProfileUpsertCommandV2Schema = z.object({
    commandId: workflowIdSchema,
    eventType: z.literal('workflow.canteen.dietary-profile-upsert-requested.v2'),
    schemaVersion: z.literal(2),
    institutionId: workflowIdSchema,
    requesterId: workflowIdSchema,
    caseId: workflowIdSchema,
    correlationId: workflowIdSchema,
    idempotencyKey: z.string().min(16).max(200),
    issuedAt: workflowDateTimeSchema,
    effect: z.object({
        effectType: z.literal('dietary_exception.apply'),
        consumerId: workflowIdSchema,
        expectedVersion: z.number().int().nonnegative(),
        input: upsertDietaryProfileSchema,
    }),
});
export const canteenWorkflowEffectResultV2Schema = canteenWorkflowEffectResultSchema.extend({
    institutionId: workflowIdSchema,
    schemaVersion: z.literal(2),
});
export const workflowEventSchema = z.object({
    eventId: workflowIdSchema,
    eventType: z.enum([
        'workflow.case.created.v1',
        'workflow.case.transitioned.v1',
        'workflow.decision.recorded.v1',
        'workflow.canteen.effect-requested.v1',
        'workflow.canteen.effect-result.v1',
    ]),
    schemaVersion: z.literal(1),
    aggregateId: workflowIdSchema,
    aggregateVersion: z.number().int().positive(),
    institutionId: workflowIdSchema,
    correlationId: workflowIdSchema,
    occurredAt: workflowDateTimeSchema,
    payload: z.record(z.string(), z.unknown()),
});
export const workflowDryRunSchema = z.object({
    definition: workflowDefinitionDraftSchema,
    sample: createWorkflowCaseSchema.omit({
        definitionId: true,
        definitionVersion: true,
    }),
});
export const workflowMigrationDryRunSchema = z.object({
    fromDefinitionId: workflowIdSchema,
    fromVersion: z.number().int().positive(),
    toDefinitionId: workflowIdSchema,
    toVersion: z.number().int().positive(),
    caseIds: z.array(workflowIdSchema).min(1).max(500),
    stateMap: z.record(z.string(), workflowCaseStateSchema),
});
